import { gql, useMutation, useQuery } from '@apollo/client'
import isEqual from 'lodash/isEqual'

import {
  ArtistUpdateEventInput,
  ArtistUpdateEventMutation,
  ArtistUpdateEventMutationVariables,
  ConsoleEventDetailFragmentDoc,
  ConsoleEventEditDetailFragmentDoc,
  ConsoleEventEditQuery,
  ConsoleEventEditQueryVariables,
  EventStatus,
} from '@/clients/utoniq-core/schema'
import { CreateEventValue } from '@/components/pages/ConsoleEventCreate/template/EventCreateForm'
import { useViewModelEvent } from '@/components/pages/ConsoleEventCreate/viewModel'
import { defaultValues } from '@/components/pages/ConsoleEventEdit/template/EventEditForm'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { encodeUrl } from '@/lib/url'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleEventEdit')
  const { user } = useCurrentUser()
  const { showMessage, showError } = useMessage()
  const { router } = useRouter<{ eventId: string }>()
  const { state, actions } = useViewModelEvent(user?.uid ?? '')

  const { data } = useQuery<
    ConsoleEventEditQuery,
    ConsoleEventEditQueryVariables
  >(QUERY, { variables: { id: router.query.eventId } })

  const [artistUpdateEvent, {}] = useMutation<
    ArtistUpdateEventMutation,
    ArtistUpdateEventMutationVariables
  >(ARTIST_UPDATE_EVENT)

  return {
    state: {
      ...state,
      artistId: user?.uid ?? '',
      event: data?.event ?? null,
    },
    actions: {
      ...actions,
      async onSubmit(values) {
        const input = {
          ...buildEventUpdate(values),
          thumbnailPath:
            values.thumbnailPath === data?.event?.thumbnail?.mediumUri
              ? undefined
              : values.thumbnailPath,
        }
        await artistUpdateEvent({
          variables: { id: router.query.eventId, input },
        })
          .then(() => {
            router.goBack()
            showMessage(t('updateEventSuccess'), 'success')
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleEventEdit($id: ID!) {
    event(id: $id) {
      ...ConsoleEventEditDetail
    }
  }
  ${ConsoleEventEditDetailFragmentDoc}
`

const ARTIST_UPDATE_EVENT = gql`
  mutation ArtistUpdateEvent($id: ID!, $input: ArtistUpdateEventInput!) {
    artistUpdateEvent(id: $id, inputs: $input) {
      ...ConsoleEventDetail
    }
  }
  ${ConsoleEventDetailFragmentDoc}
`

function buildEventUpdate(values: CreateEventValue): ArtistUpdateEventInput {
  return {
    title: values.title,
    description: values.description,
    note: values.note || null,
    location: getLocation(values?.location) || undefined,
    startedAt: values?.startedAt || null,
    endedAt: values?.endedAt || null,
    status: values.isPublished
      ? EventStatus.Published
      : EventStatus.Unpublished,
    closeDate:
      values.closeDate?.filter(({ date }) => !!date).map(({ date }) => date) ||
      null,
  }
}

/**
 * @param location
 */
export function getLocation<T extends ArtistUpdateEventInput['location']>(
  location: T
): T | null {
  if (!location || isEqual(location, defaultValues.location)) {
    return null
  }
  return {
    ...location,
    mapUri: encodeUrl(location.mapUri),
  }
}
